// New-order dialog (F-FE-N97). Three ways in: the queue's "New order" button
// (blank), the Alerts tab's per-vendor "Create PO" handoff (pre-filled lines),
// and ?orderFor=<vendor> from a vendor profile (vendor preselected). Once a
// vendor is picked, the items you're running low on from THEM are one tap
// away — the order starts from reality, not a blank item picker.
//
// The old button said "Create draft" for a state the backend never creates:
// an admin's order is born ready-to-send, everyone else's goes for approval.
// The button now says which.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import apiClient from '@/api/client'
import type { Supplier, StockItem, StockAlert } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { cn } from '@/lib/utils'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { ItemSearchPicker } from './purchasing/ItemSearchPicker'
import {
  fromBaseCost, fromBaseQty, poLineQtyText, resolvePack, roundUpToPacks, startingCost, toBaseCost, toBaseQty, type Pack,
} from './purchasing/purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasing/purchasingStrings'

type ToastFn = (type: 'success' | 'error', message: string) => void

/** Pre-filled new-order draft (Alerts tab handoff / vendor-profile entry). */
export type PODraft = {
  supplier_id: string
  notes?: string
  lines: { stock_item_id: string; quantity_ordered: number; unit_cost?: number }[]
}

/** quantity_ordered / unit_cost are always in the item's base unit (what the
 *  PO stores). inPacks (kitchen flow pilot) only changes how they are typed
 *  and shown: "2 bags" ↔ 50 kg, "2,500 per bag" ↔ 100 per kg. */
type FormLine = { stock_item_id: string; quantity_ordered: number; unit_cost: number | undefined; inPacks?: boolean }

const suggestCost = (a: StockAlert): number | undefined =>
  a.last_purchase_cost ?? a.wac_unit_cost ?? (a.default_unit_cost > 0 ? a.default_unit_cost : undefined)

const BLANK_LINE: FormLine = { stock_item_id: '', quantity_ordered: 1, unit_cost: undefined }

export function PoCreateDialog({
  open,
  onOpenChange,
  suppliers,
  stockItems,
  alerts,
  initialDraft,
  isAdmin,
  showToast,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  suppliers: Supplier[]
  stockItems: StockItem[]
  alerts: StockAlert[]
  initialDraft: PODraft | null
  isAdmin: boolean
  showToast: ToastFn
  onCreated: (id: string) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const [supplierId, setSupplierId] = useState('')
  const [expectedDate, setExpectedDate] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<FormLine[]>([BLANK_LINE])

  // Kitchen flow lane D: the vendor's catalogue pre-fills a picked item's
  // price and shows its pack. Not fetched (and nothing changes) with the flag off.
  const kitchenFlow = useKitchenFlowEnabled()
  const { data: vendorItems } = useQuery({
    queryKey: ['vendorItems', supplierId],
    queryFn: () => apiClient.listVendorItems(supplierId).then((r) => r.data ?? []),
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
    enabled: open && kitchenFlow && !!supplierId,
  })
  const catalogue = useMemo(() => new Map((vendorItems ?? []).map((v) => [v.stock_item_id, v])), [vendorItems])
  const itemsById = useMemo(() => new Map(stockItems.map((it) => [it.id, it])), [stockItems])

  // Kitchen flow pilot (Batch 5): the pack a line can be ordered in — the
  // vendor's catalogue pack, else the item's buying unit. Flag off: none.
  const packFor = (itemId: string): Pack | null => {
    if (!kitchenFlow || !itemId) return null
    const vi = catalogue.get(itemId)
    const it = itemsById.get(itemId)
    return resolvePack({ vendorLabel: vi?.pack_label, vendorQty: vi?.pack_qty, itemLabel: it?.purchase_unit_label, itemFactor: it?.purchase_unit_factor })
  }
  // A fresh line for an item: its own price (never the previous item's), in
  // packs when it has one, rounded up to whole packs.
  const lineFor = (itemId: string, baseQty: number, cost?: number): FormLine => {
    const pack = packFor(itemId)
    const it = itemsById.get(itemId)
    return {
      stock_item_id: itemId,
      quantity_ordered: pack ? roundUpToPacks(baseQty, pack.qty) : baseQty,
      unit_cost: cost ?? startingCost({ catalogue: catalogue.get(itemId)?.last_price, wac: it?.wac_unit_cost, defaultCost: it?.default_unit_cost }),
      inPacks: pack != null,
    }
  }

  // Seed the form each time the dialog opens (from the handoff draft or blank).
  useEffect(() => {
    if (!open) return
    if (initialDraft) {
      setSupplierId(initialDraft.supplier_id)
      setNotes(initialDraft.notes ?? '')
      setLines(
        initialDraft.lines.length > 0
          ? initialDraft.lines.map((l) => ({ stock_item_id: l.stock_item_id, quantity_ordered: l.quantity_ordered, unit_cost: l.unit_cost }))
          : [BLANK_LINE],
      )
    } else {
      setSupplierId('')
      setNotes('')
      setLines([BLANK_LINE])
    }
    setExpectedDate('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // What this vendor is running low on, minus lines already in the order.
  const vendorSuggestions = useMemo(() => {
    if (!supplierId) return []
    const inOrder = new Set(lines.map((l) => l.stock_item_id))
    return alerts.filter((a) => a.supplier_id === supplierId && a.suggested_qty > 0 && !inOrder.has(a.id))
  }, [alerts, supplierId, lines])

  // No vendor picked yet: offer the vendors with low stock as starting points.
  const lowVendorGroups = useMemo(() => {
    if (supplierId) return []
    const m = new Map<string, { id: string; name: string; alerts: StockAlert[] }>()
    for (const a of alerts) {
      if (!a.supplier_id || a.suggested_qty <= 0) continue
      let g = m.get(a.supplier_id)
      if (!g) {
        g = { id: a.supplier_id, name: a.supplier_name ?? 'Vendor', alerts: [] }
        m.set(a.supplier_id, g)
      }
      g.alerts.push(a)
    }
    return [...m.values()]
  }, [alerts, supplierId])

  const addSuggestion = (a: StockAlert) => {
    const line: FormLine = lineFor(a.id, Math.max(Math.ceil(a.suggested_qty), 1), suggestCost(a))
    setLines((ls) => {
      const kept = ls.filter((l) => l.stock_item_id !== '' || l.quantity_ordered !== 1 || l.unit_cost !== undefined)
      return [...kept, line]
    })
  }

  const seedVendor = (g: { id: string; alerts: StockAlert[] }) => {
    setSupplierId(g.id)
    setLines(g.alerts.map((a) => lineFor(a.id, Math.max(Math.ceil(a.suggested_qty), 1), suggestCost(a))))
  }

  const total = useMemo(
    () => lines.reduce((s, l) => s + (l.unit_cost != null && l.quantity_ordered > 0 ? l.quantity_ordered * l.unit_cost : 0), 0),
    [lines],
  )

  const createPO = useMutation({
    mutationFn: async () => {
      const res = await apiClient.createPurchaseOrder({
        supplier_id: supplierId,
        expected_date: expectedDate || undefined,
        notes: notes || undefined,
        lines: lines
          .filter((l) => l.stock_item_id && l.quantity_ordered > 0)
          .map((l) => ({ stock_item_id: l.stock_item_id, quantity_ordered: l.quantity_ordered, unit_cost: l.unit_cost })),
      })
      if (!res.success || !res.data?.id) throw new Error(res.error || res.message || 'Failed to create order')
      return res.data.id
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] }) // F-FE-N118: on_order_qty on the Items tab
      showToast('success', isAdmin ? 'Order created — ready to send' : 'Sent for admin approval')
      onOpenChange(false)
      onCreated(newId)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const supplierName = suppliers.find((s) => s.id === supplierId)?.name

  // Kitchen flow pilot (Batch 5): scan an item to add it (one pack, or the
  // barcode's pack size), as on the other stock screens.
  const scanner = useItemScanner({
    enabled: kitchenFlow && open,
    onItem: (m) => {
      if (!itemsById.has(m.item.id)) return { ok: false, text: IM.scan.notOnList(m.item.name, 'order') }
      const step = packFor(m.item.id)?.qty ?? (m.pack_qty || 1)
      setLines((ls) => {
        const i = ls.findIndex((l) => l.stock_item_id === m.item.id)
        if (i >= 0) return ls.map((l, j) => (j === i ? { ...l, quantity_ordered: Math.round((l.quantity_ordered + step) * 1000) / 1000 } : l))
        const kept = ls.filter((l) => l.stock_item_id !== '')
        return [...kept, lineFor(m.item.id, step)]
      })
      return { ok: true, text: m.item.name }
    },
  })
  const setItem = (idx: number, itemId: string) => {
    setLines((ls) => {
      const next = [...ls]
      const prev = next[idx]
      // Pilot fix (Batch 5): a new item starts at its own price — the old
      // code kept the previous item's cost. A typed quantity is kept.
      const fresh = lineFor(itemId, 1)
      const pack = packFor(itemId)
      const keepQty = prev.stock_item_id !== '' || prev.quantity_ordered !== 1
      next[idx] = {
        ...fresh,
        quantity_ordered: keepQty ? toBaseQty(fromBaseQty(prev.quantity_ordered, Boolean(prev.inPacks), packFor(prev.stock_item_id)), pack != null, pack) : fresh.quantity_ordered,
      }
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">New order{supplierName ? ` — ${supplierName}` : ''}</DialogTitle>
          <DialogDescription>
            This order goes to the vendor. Stock only changes when you receive the delivery.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-1 text-sm">
          <div className="space-y-2">
            <Label htmlFor="po-supplier">Vendor</Label>
            <select
              id="po-supplier"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">Select vendor…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {lowVendorGroups.length > 0 && (
            <div className="rounded-lg border border-info-border bg-info-surface p-3 text-sm">
              <p className="font-medium text-info-fg">Running low right now</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {lowVendorGroups.map((g) => (
                  <Button key={g.id} size="sm" variant="outline" className="h-8 bg-background text-xs" onClick={() => seedVendor(g)}>
                    {g.name} · {g.alerts.length} item{g.alerts.length === 1 ? '' : 's'}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {vendorSuggestions.length > 0 && (
            <div className="rounded-lg border border-info-border bg-info-surface p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-info-fg">
                  {vendorSuggestions.length} item{vendorSuggestions.length === 1 ? '' : 's'} you&apos;re running low on from {supplierName}
                </p>
                <Button size="sm" variant="outline" className="h-7 bg-background text-xs" onClick={() => vendorSuggestions.forEach(addSuggestion)}>
                  Add all
                </Button>
              </div>
              <ul className="mt-2 space-y-1">
                {vendorSuggestions.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {a.name} <span className="text-muted-foreground">· suggest {Math.ceil(a.suggested_qty)} {a.unit}</span>
                    </span>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" aria-label={`Add ${a.name}`} onClick={() => addSuggestion(a)}>
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-semibold">Items</Label>
            {scanner.ui}
            {/* The search picker's list drops below its row, so the table
                must not clip it (kitchen flow only). */}
            <div className={kitchenFlow ? 'rounded-md border' : 'rounded-md border overflow-hidden'}>
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left font-semibold text-sm px-3 py-2.5">Item</th>
                    <th className={cn('text-right font-semibold text-sm px-3 py-2.5', kitchenFlow ? 'w-[230px]' : 'w-[170px]')}>Qty</th>
                    <th className="text-right font-semibold text-sm px-3 py-2.5 w-[130px]">Unit cost</th>
                    <th className="w-[100px] px-2 py-2.5" aria-label="Row actions" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {lines.map((ln, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 align-middle">
                        {kitchenFlow ? (
                          <ItemSearchPicker items={stockItems} value={ln.stock_item_id} onChange={(id) => setItem(idx, id)} />
                        ) : (
                          <select
                            className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                            value={ln.stock_item_id}
                            onChange={(e) => setItem(idx, e.target.value)}
                          >
                            <option value="">Select item…</option>
                            {stockItems.map((it) => (
                              <option key={it.id} value={it.id}>{it.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        {(() => {
                          const pack = packFor(ln.stock_item_id)
                          const inPacks = Boolean(ln.inPacks && pack)
                          const unit = itemsById.get(ln.stock_item_id)?.unit ?? ''
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <Input
                                  type="number"
                                  className="text-right tabular-nums flex-1 min-w-0"
                                  placeholder="Qty"
                                  aria-label={inPacks && pack ? `Qty in ${pack.label}` : 'Qty'}
                                  value={ln.quantity_ordered ? fromBaseQty(ln.quantity_ordered, inPacks, pack) : ''}
                                  onChange={(e) => {
                                    const next = [...lines]
                                    next[idx] = { ...next[idx], quantity_ordered: toBaseQty(parseFloat(e.target.value) || 0, inPacks, pack) }
                                    setLines(next)
                                  }}
                                />
                                {pack ? (
                                  // Kitchen flow pilot (Batch 5): count this line in packs or base units.
                                  <select
                                    aria-label="Count in"
                                    className="h-10 shrink-0 rounded-md border border-input bg-background px-2 text-sm"
                                    value={inPacks ? 'pack' : 'unit'}
                                    onChange={(e) => {
                                      const next = [...lines]
                                      next[idx] = { ...next[idx], inPacks: e.target.value === 'pack' }
                                      setLines(next)
                                    }}
                                  >
                                    <option value="unit">{PURCHASING_STRINGS.packs.units(unit)}</option>
                                    <option value="pack">{pack.label}</option>
                                  </select>
                                ) : (
                                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                    {unit}
                                    {catalogue.get(ln.stock_item_id)?.pack_label ? ` · ${catalogue.get(ln.stock_item_id)!.pack_label}` : ''}
                                  </span>
                                )}
                              </div>
                              {pack && ln.quantity_ordered > 0 && (
                                <p className="text-right text-xs text-muted-foreground tabular-nums">
                                  {PURCHASING_STRINGS.packs.equals(inPacks ? `${fromBaseQty(ln.quantity_ordered, false, pack)} ${unit}` : poLineQtyText(ln.quantity_ordered, unit, pack))}
                                </p>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        {(() => {
                          const pack = packFor(ln.stock_item_id)
                          const inPacks = Boolean(ln.inPacks && pack)
                          return (
                            <div className="space-y-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                className="text-right tabular-nums"
                                placeholder="Optional"
                                value={ln.unit_cost != null ? fromBaseCost(ln.unit_cost, inPacks, pack) : ''}
                                onChange={(e) => {
                                  const next = [...lines]
                                  const v = e.target.value
                                  next[idx] = { ...next[idx], unit_cost: v === '' ? undefined : toBaseCost(parseFloat(v), inPacks, pack) }
                                  setLines(next)
                                }}
                              />
                              {inPacks && pack && <p className="text-right text-xs text-muted-foreground">{PURCHASING_STRINGS.packs.perPack(pack.label)}</p>}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <div className="flex gap-1 justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            aria-label="Add line"
                            onClick={() => setLines((ls) => [...ls, BLANK_LINE])}
                          >
                            +
                          </Button>
                          {lines.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 shrink-0"
                              aria-label="Remove line"
                              onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                            >
                              −
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="po-expected">Expected delivery</Label>
              <DatePicker id="po-expected" value={expectedDate} onChange={setExpectedDate} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="po-notes">Notes</Label>
              <Textarea id="po-notes" className="min-h-[44px] resize-y" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional…" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:items-center sm:gap-3">
          {total > 0 && (
            <span className="text-sm text-muted-foreground sm:mr-auto">
              Order value <span className="font-semibold text-foreground tabular-nums">{formatCurrency(total)}</span>
            </span>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!supplierId || lines.every((l) => !l.stock_item_id) || lines.some((l) => l.unit_cost != null && !(l.unit_cost >= 0)) || createPO.isPending}
            onClick={() => createPO.mutate()}
          >
            {createPO.isPending ? 'Saving…' : isAdmin ? 'Create order' : 'Send for approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
